import { useState } from 'react'
import { MessageSquarePlus, X, Send } from 'lucide-react'
import { api } from '../../api/client'
import { useToast } from './Toast'

export function FeedbackPanel() {
  const [open, setOpen] = useState(false)
  const [category, setCategory] = useState('other')
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const { showToast } = useToast()

  const handleSubmit = async () => {
    if (!message.trim()) return
    setSubmitting(true)
    try {
      await api.post('/api/feedback', { category, message: message.trim() })
      showToast('success', 'Thank you for your feedback!')
      setMessage('')
      setCategory('other')
      setOpen(false)
    } catch {
      showToast('error', 'Failed to submit feedback. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-indigo-600 text-white shadow-lg transition-all duration-200 hover:bg-indigo-500 hover:shadow-xl hover:scale-105 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-slate-950"
        aria-label="Send feedback"
        title="Send feedback"
      >
        <MessageSquarePlus className="h-5 w-5" />
      </button>

      {/* Overlay + Panel */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-end p-6">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/20 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />

          {/* Panel */}
          <div className="relative w-full max-w-md animate-slide-up rounded-2xl border border-slate-700 bg-slate-900 p-6 shadow-2xl">
            {/* Header */}
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Send Feedback</h3>
              <button
                onClick={() => setOpen(false)}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                aria-label="Close feedback panel"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Category */}
            <label className="mb-1 block text-sm font-medium text-slate-300">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mb-4 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-200 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="bug">Bug</option>
              <option value="suggestion">Suggestion</option>
              <option value="other">Other</option>
            </select>

            {/* Message */}
            <label className="mb-1 block text-sm font-medium text-slate-300">Your message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us what's on your mind..."
              rows={4}
              className="mb-4 w-full resize-none rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={submitting || !message.trim()}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-indigo-500 disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              {submitting ? 'Sending...' : 'Submit Feedback'}
            </button>
          </div>
        </div>
      )}
    </>
  )
}
